import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider.jsx";
import { useActiveStore } from "../lib/activeStore.js";
import { canManage } from "../lib/roles.js";
import { bulkImportUnits, archiveAbsentUnits, authorFromProfile } from "../lib/data.js";

// PDF parser is ~600 kB once pdfjs-dist is bundled in. Lazy-load it so the
// 95% of users who never hit the Import page (lot techs, managers reading
// the dashboard) don't pay the cost on first paint. Stash the resolved
// module so we only fetch it once per session.
let _darParserPromise = null;
function loadDarParser() {
  if (!_darParserPromise) {
    _darParserPromise = import("../lib/darParser.js");
  }
  return _darParserPromise;
}

// Import DAR — drag-and-drop a Pipeline Detail PDF, parse it spatially,
// preview the extracted vehicles, then commit. Idempotent on (tenantId,
// storeId, stockNumber) — re-importing the same DAR updates `daysOnLot`,
// `onLotSince`, sold flag, model/chassis without touching voltage/history.
export function ImportDar() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  // Import into the actively-viewed store (topbar switcher), not the default
  // profile.storeId — otherwise a DAR imported after a store switch silently
  // lands in the wrong store.
  const activeStore = useActiveStore() || profile?.storeId || null;

  const [phase, setPhase] = useState("idle"); // idle | parsing | preview | committing | done | error
  const [error, setError] = useState(null);
  const [parsed, setParsed] = useState(null);   // { entries, totalParsed, droppedNoStock, rawItemCount }
  const [result, setResult] = useState(null);   // { created, updated, skipped, errors[] }
  const [dragOver, setDragOver] = useState(false);

  if (!canManage(profile)) {
    return (
      <main className="container">
        <div className="panel" style={{ maxWidth: 480, margin: "60px auto" }}>
          <div className="eyebrow">Restricted</div>
          <h2 style={{ marginTop: 10 }}>Importing inventory is admin-only.</h2>
          <p style={{ marginTop: 10 }}>
            Ask a store admin to grant you the manager role on Battery Care if you need to import a DAR.
          </p>
          <Link to="/today" className="btn" style={{ marginTop: 14 }}>← Today</Link>
        </div>
      </main>
    );
  }

  async function handleFile(file) {
    if (!file) return;
    if (!/\.pdf$/i.test(file.name)) {
      setError(new Error("File must be a .pdf"));
      setPhase("error");
      return;
    }
    setError(null);
    setPhase("parsing");
    try {
      const { parseDarPdf } = await loadDarParser();
      const out = await parseDarPdf(file);
      setParsed(out);
      setPhase(out.entries.length > 0 ? "preview" : "error");
      if (out.entries.length === 0) {
        setError(new Error(
          "Couldn't parse any vehicles from this PDF. Confirm it's the BMW DAR Pipeline Detail report — " +
          "the parser is hardcoded to that template's column layout."
        ));
      }
    } catch (err) {
      setError(err);
      setPhase("error");
    }
  }

  async function commit() {
    if (!parsed?.entries?.length) return;
    if (!profile?.tenantId || !activeStore) {
      setError(new Error("Your account isn't linked to a store yet. Ask an admin to set your tenant + store, then try again."));
      setPhase("error");
      return;
    }
    setPhase("committing");
    setError(null);
    try {
      const ctx = {
        tenantId:   profile.tenantId,
        storeId:    activeStore,
        department: "new-car",
        by:         authorFromProfile(profile),
      };
      const { darEntryToUnit } = await loadDarParser();
      const units = parsed.entries.map((e) => ({
        ...darEntryToUnit(e, ctx),
      }));
      const out = await bulkImportUnits(units, ctx);
      // Auto-archive: every unit in /batteryUnits for this rooftop+dept whose
      // stockNumber is NOT in this DAR gets soft-archived with reason
      // "left-the-DAR". This is what makes the lot self-clean: when a car
      // sells, it drops off the next DAR and disappears from Walk / Today /
      // Inventory automatically. The unit row stays in Firestore for history.
      try {
        const stockNumbers = units.map((u) => u.stockNumber).filter(Boolean);
        const archiveOut = await archiveAbsentUnits(stockNumbers, ctx);
        out.archived = archiveOut.archived;
        if (archiveOut.errors?.length) {
          out.errors = [...(out.errors || []), ...archiveOut.errors];
        }
      } catch (archiveErr) {
        // Don't fail the whole import if the archive step blows up — the
        // freshly-imported / updated rows are already in. Surface the error.
        out.archived = 0;
        out.errors = [
          ...(out.errors || []),
          { phase: "archive", error: archiveErr.message || String(archiveErr) },
        ];
      }
      setResult(out);
      setPhase("done");
    } catch (err) {
      setError(err);
      setPhase("error");
    }
  }

  function reset() {
    setPhase("idle");
    setError(null);
    setParsed(null);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  }

  return (
    <main className="container tight">
      <Link to="/manager" className="ci-back">← Manager</Link>

      <div className="eyebrow" style={{ marginTop: 18 }}>Import DAR</div>
      <h1 style={{ fontSize: 22, marginTop: 6 }}>Pipeline Detail PDF</h1>
      <p style={{ marginTop: 6, fontSize: 13, color: "var(--text-muted)" }}>
        Drop the BMW DAR (Pipeline Detail) PDF here. Re-importing the same DAR
        updates days-on-lot and sold flags without touching voltage history.
      </p>

      {/* IDLE / PARSING — drag-and-drop zone */}
      {(phase === "idle" || phase === "parsing") && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{
            marginTop: 22,
            padding: "60px 24px",
            border: `2px dashed ${dragOver ? "var(--brand-cobalt)" : "var(--line-strong)"}`,
            borderRadius: 18,
            background: dragOver ? "rgba(122,167,230,0.08)" : "var(--panel)",
            textAlign: "center",
            cursor: "pointer",
            transition: "all 0.15s ease",
          }}
        >
          {phase === "parsing" ? (
            <>
              <div className="boot" style={{ minHeight: "auto", padding: 0 }}>
                <div className="spinner" />
                <div className="label">Parsing PDF…</div>
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 36, marginBottom: 12, color: "var(--brand-cobalt)" }}>📄</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text)" }}>
                Click or drag a DAR PDF here
              </div>
              <div style={{ fontSize: 12.5, color: "var(--text-dim)", marginTop: 8 }}>
                Pipeline Detail · BMW NA template · charge history preserved on re-import
              </div>
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            style={{ display: "none" }}
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
        </div>
      )}

      {/* ERROR */}
      {phase === "error" && (
        <div className="panel" style={{
          marginTop: 22,
          borderColor: "rgba(124,41,53,0.3)",
          color: "var(--danger)",
        }}>
          <div className="eyebrow" style={{ color: "var(--danger)" }}>Error</div>
          <p style={{ marginTop: 8, color: "var(--text)" }}>
            {error?.message || String(error)}
          </p>
          <div style={{ marginTop: 14 }}>
            <button onClick={reset} className="btn">Try again</button>
          </div>
        </div>
      )}

      {/* PREVIEW — show parsed entries before committing */}
      {phase === "preview" && parsed && (
        <>
          <div className="panel" style={{ marginTop: 22 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 12 }}>
              <div>
                <div className="eyebrow">Parsed</div>
                <h2 style={{ marginTop: 6 }}>
                  {parsed.entries.length} vehicle{parsed.entries.length === 1 ? "" : "s"} ready to import
                </h2>
                <p style={{ marginTop: 6, fontSize: 12.5, color: "var(--text-muted)" }}>
                  Re-imports merge into existing units by stock # — voltage and check history preserved.
                  {parsed.droppedNoStock > 0 && (
                    <> {parsed.droppedNoStock} row{parsed.droppedNoStock === 1 ? "" : "s"} skipped (no stock #).</>
                  )}
                </p>
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={reset} className="btn">Cancel</button>
                <button onClick={commit} className="btn primary">
                  Import {parsed.entries.length} unit{parsed.entries.length === 1 ? "" : "s"}
                </button>
              </div>
            </div>
          </div>

          <div className="panel flush" style={{ marginTop: 14 }}>
            <table className="bc">
              <thead>
                <tr>
                  <th>Stock #</th>
                  <th>Model</th>
                  <th>Chassis</th>
                  <th className="right">Days</th>
                  <th>Invoice date</th>
                  <th>Sold</th>
                </tr>
              </thead>
              <tbody>
                {parsed.entries.slice(0, 100).map((e, i) => (
                  <tr key={i}>
                    <td className="mono">{e.stockNo}</td>
                    <td>{e.model}</td>
                    <td className="mono" style={{ color: "var(--text-muted)", fontSize: 12 }}>{e.chassis}</td>
                    <td className="mono right">{e.days}</td>
                    <td style={{ color: "var(--text-muted)", fontSize: 12.5 }}>{e.invoiceDate || "—"}</td>
                    <td>
                      {e.sold ? <span className="pill warn">Sold</span> : <span className="pill muted">—</span>}
                    </td>
                  </tr>
                ))}
                {parsed.entries.length > 100 && (
                  <tr>
                    <td colSpan={6} style={{ textAlign: "center", color: "var(--text-dim)", fontSize: 12.5 }}>
                      … and {parsed.entries.length - 100} more (preview shows first 100)
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* COMMITTING */}
      {phase === "committing" && (
        <div className="panel" style={{ marginTop: 22 }}>
          <div className="boot" style={{ minHeight: "auto", padding: "12px 0" }}>
            <div className="spinner" />
            <div className="label">Importing {parsed?.entries?.length || 0} units…</div>
          </div>
        </div>
      )}

      {/* DONE */}
      {phase === "done" && result && (
        <div className="panel" style={{ marginTop: 22, borderColor: "rgba(74,222,128,0.30)" }}>
          <div className="eyebrow" style={{ color: "var(--ok)" }}>✓ Imported</div>
          <h2 style={{ marginTop: 8 }}>
            {result.created} new · {result.updated} updated
            {result.skipped > 0 ? ` · ${result.skipped} skipped` : ""}
            {result.archived > 0 ? ` · ${result.archived} archived` : ""}
          </h2>
          {result.archived > 0 && (
            <p style={{ marginTop: 6, fontSize: 13, color: "var(--text-muted)" }}>
              {result.archived} unit{result.archived === 1 ? "" : "s"} were on the lot before this import but aren't in the new DAR &mdash; we marked them sold so they drop off Walk / Today / Inventory. The history is preserved.
            </p>
          )}
          {result.errors.length > 0 && (
            <div style={{ marginTop: 12, fontSize: 12.5, color: "var(--warn)" }}>
              {result.errors.length} error{result.errors.length === 1 ? "" : "s"}:
              <ul style={{ marginTop: 6 }}>
                {result.errors.slice(0, 5).map((e, i) => (
                  <li key={i}><span className="mono">{e.stockNumber}</span>: {e.error}</li>
                ))}
                {result.errors.length > 5 && <li>… and {result.errors.length - 5} more</li>}
              </ul>
            </div>
          )}
          <div style={{ marginTop: 18, display: "flex", gap: 10 }}>
            <Link to="/manager" className="btn primary">View manager dashboard</Link>
            <Link to="/inventory" className="btn">Go to lot walk</Link>
            <button onClick={reset} className="btn">Import another</button>
          </div>
        </div>
      )}
    </main>
  );
}
